<template>
    <div data-test-view="goals-edit" class="tw-p-4">
        <div class="tw-grid tw-grid-cols-12 tw-space-x-4 tw-mb-8">
            <div
                class="tw-col-span-6 tw-border tw-border-solid tw-rounded-lg tw-p-4 tw-cursor-pointer"
                :class="
                    !goal.isDevelopmentGoal
                        ? 'tw-border-blue-200 tw-bg-blue-50'
                        : 'tw-border-gray-200'
                "
                @click="toggleDevelopmentGoal(false)"
                @keyup.enter="toggleDevelopmentGoal(false)"
            >
                <div class="tw-mb-2" :class="!goal.isDevelopmentGoal ? 'tw-font-bold' : ''">
                    <i class="fal fa-bullseye tw-mr-2"></i>
                    {{ $t("goals.update.typeBusinessGoal") }}
                </div>
                <div class="tw-text-gray-500">
                    {{ $t("goals.update.typeBusinessGoalExplanation") }}
                </div>
            </div>
            <div
                class="tw-col-span-6 tw-border tw-border-solid tw-rounded-lg tw-p-4 tw-cursor-pointer"
                :class="
                    goal.isDevelopmentGoal
                        ? 'tw-border-green-200 tw-bg-green-50'
                        : 'tw-border-gray-200'
                "
                @click="toggleDevelopmentGoal(true)"
                @keyup.enter="toggleDevelopmentGoal(true)"
            >
                <div class="tw-mb-2" :class="goal.isDevelopmentGoal ? 'tw-font-bold' : ''">
                    <i class="fal fa-seedling tw-mr-2"></i>
                    {{ $t("goals.update.typeDevelopmentGoal") }}
                </div>
                <div class="tw-text-gray-500">
                    {{ $t("goals.update.typeDevelopmentGoalExplanation") }}
                </div>
            </div>
        </div>

        <!--/Header -->

        <!-- Form -->

        <!-- Title -->
        <div @mouseover="helpSection = 'title'" @focus="helpSection = 'title'">
            <!-- Name -->
            <l-input-text
                ref="input-name"
                v-model="goal.name"
                type="text"
                :label="``"
                :placeholder="$t(`goals.update.placeholderTitle`)"
                :disabled="!goal.access.canUpdateGoal"
            />

            <!-- Description -->
            <l-input-text
                ref="input-description"
                v-model="goal.description"
                type="textarea"
                :label="``"
                :rows="1"
                :placeholder="$t(`goals.update.placeholderDescription`)"
                :disabled="!goal.access.canUpdateGoal"
                optional
                hide-help-block
                style="margin-top: -10px"
            />

            <div class="row">
                <!-- Tags -->
                <div class="col-md-4">
                    <l-input-typeahead
                        :label="``"
                        :placeholder="$t(`goals.update.placeholderTag`)"
                        :disabled="!goal.access.canUpdateGoal"
                        :show-picture="false"
                        :data="tags"
                        :on-select="typeaheadTagsOnSelect"
                        :on-input-change="typeaheadTagsOnInputChange"
                    />
                    <div v-if="goal.tags.length" style="margin-bottom: 20px">
                        <span v-for="(tag, index) in goal.tags" :key="tag._id">
                            <span
                                class="label label-primary clickable r-default-margin-s"
                                tabindex="0"
                                @click="removeTag(index)"
                                @keyup.enter="removeTag(index)"
                            >
                                {{ tag.name }}
                                <i class="fal fa-times l-default-margin-s"></i>
                            </span>
                        </span>
                    </div>
                </div>
                <!--/Tags -->

                <!-- Cycle -->
                <div
                    v-show="
                        (optionsCycle.length || (goal.cycle && goal.cycle._id)) &&
                            !(goalType === 'goalMe' && goal.visibility === 'me')
                    "
                    class="col-md-4"
                >
                    <l-select
                        style="margin-top: 10px;"
                        :value="goal.cycle ? goal.cycle._id : ``"
                        :options="optionsCycle"
                        :disabled="!goal.access.canUpdateGoal"
                        :label="``"
                        @change="e => selectCycle(e.target.value)"
                    />
                </div>

                <!-- Deadline -->
                <div :class="[user.rights.G_ADD_WEIGHTS ? 'col-md-2' : 'col-md-4']">
                    <l-date-picker
                        style="margin-top: 10px;"
                        :label="''"
                        :placeholder="$t(`goals.update.placeholderDeadline`)"
                        :value="goal.deadline ? new Date(goal.deadline) : null"
                        :on-date-change="
                            date => {
                                goal.deadline = date;
                            }
                        "
                    />
                </div>
                <!--/Deadline -->

                <!-- Weights -->
                <div v-show="user.rights.G_ADD_WEIGHTS" class="col-md-2">
                    <l-input-text
                        v-model="goal.metricWeight"
                        style="margin-top: 10px;"
                        type="text"
                        :label="``"
                        :placeholder="$t(`goals.update.placeholderMetricWeight`)"
                        :disabled="!goal.access.canUpdateGoal"
                        hide-help-block
                        optional
                    />
                </div>
                <!--/Weights -->
            </div>
            <!--/Cycle -->

            <l-space :size="1" />

            <!-- Icon -->
            <div>
                <span
                    v-for="icon in icons"
                    :key="icon"
                    v-b-popover.hover.top="$t(`goals.update.${icon}Help`)"
                    class="r-default-margin clickable"
                >
                    <img
                        :src="`/img/icons/${icon}.png`"
                        alt="Goal Icon"
                        width="40"
                        :class="{
                            'semi-transparent': goal.icon !== `/img/icons/${icon}.png`,
                        }"
                        tabindex="0"
                        @click="setIcon(icon)"
                        @keyup.enter="setIcon(icon)"
                    />
                </span>
            </div>
            <!--/Icon -->
        </div>
        <!--/Title -->

        <!-- Owner -->
        <div class="tw-mb-8" @mouseover="helpSection = 'owner'" @focus="helpSection = 'owner'">
            <h5 class="color-dark" style="margin-top: 30px">
                <strong>
                    {{ $t(`goals.update.titleOwner`) }}
                    <a
                        v-show="user.rights.A_USER && goalType === 'goalLocalTeam'"
                        class="color-primary"
                        href="/app/#/team/members/teams/list"
                        target="_blank"
                    >
                        <i class="fal fa-users l-default-margin-s"></i>
                        <span class="l-default-margin-s">
                            {{ $t(`goals.update.btnUpdateTeams`) }}
                        </span>
                    </a>
                </strong>
            </h5>

            <div class="row">
                <div class="col-md-4">
                    <l-select
                        v-model="goalType"
                        :options="optionsType"
                        :disabled="!goal.access.canUpdateGoal"
                        :class="goalType !== 'goalMe' ? 'type-select-bottom-margin' : ''"
                        @change="e => selectType(e.target.value)"
                    />

                    <!-- Direct Report -->
                    <div v-if="goalType === 'goalDirectReport'">
                        <l-input-typeahead
                            v-if="!((goal.user && goal.user._id) || !goal.access.canUpdateGoal)"
                            ref="input-report"
                            :label="$t(`goals.update.labelOwner`)"
                            :placeholder="$t(`goals.update.placeholderDirectReport`)"
                            :data="reports"
                            :on-select="typeaheadReportsOnSelect"
                            :on-input-change="typeaheadReportsOnInputChange"
                        />
                        <l-input-text
                            v-else
                            type="text"
                            :label="$t(`goals.update.labelOwner`)"
                            :value="goalUserName"
                            :on-click="
                                () => {
                                    removeOwner(`input-report`);
                                }
                            "
                            default-padding
                        />
                    </div>
                    <!--/Direct Report -->

                    <!-- Local Team -->
                    <div v-if="goalType === 'goalLocalTeam'">
                        <l-input-typeahead
                            v-if="
                                !(
                                    (goal.localTeam && goal.localTeam._id) ||
                                    !goal.access.canUpdateGoal
                                )
                            "
                            ref="input-local-team"
                            :label="$t(`goals.update.labelOwner`)"
                            :placeholder="$t(`goals.update.placeholderLocalTeam`)"
                            :show-picture="false"
                            :data="localTeams"
                            :on-select="typeaheadLocalTeamOnSelect"
                            :on-input-change="typeaheadLocalTeamOnInputChange"
                        />
                        <l-input-text
                            v-else
                            type="text"
                            :label="$t(`goals.update.labelOwner`)"
                            :value="goalTeamName"
                            :on-click="
                                () => {
                                    removeOwner(`input-local-team`);
                                }
                            "
                            default-padding
                        />
                    </div>
                    <!--/Local Team -->

                    <!-- Other User -->
                    <div v-if="goalType === 'goalOtherUser'">
                        <l-input-typeahead
                            v-if="!((goal.user && goal.user._id) || !goal.access.canUpdateGoal)"
                            ref="input-other-user"
                            :label="$t(`goals.update.labelOwner`)"
                            :placeholder="$t(`goals.update.placeholderDirectReport`)"
                            :data="users"
                            :on-select="typeaheadUserOnSelect"
                            :on-input-change="typeaheadUserOnInputChange"
                        />
                        <l-input-text
                            v-else
                            type="text"
                            :label="$t(`goals.update.labelOwner`)"
                            :value="goalUserName"
                            :on-click="
                                () => {
                                    removeOwner(`input-other-user`);
                                }
                            "
                            default-padding
                        />
                    </div>

                    <!-- Managed By -->
                    <div
                        v-if="['goalOtherUser', 'goalLocalTeam', 'goalTeam'].includes(goalType)"
                        :class="goalType !== 'goalTeam' ? 'managed-by-top-margin' : ''"
                    >
                        <l-input-typeahead
                            v-if="
                                !(
                                    (goal.managedBy && goal.managedBy._id) ||
                                    !goal.access.canUpdateGoal
                                )
                            "
                            ref="input-managed-by"
                            :label="$t(`goals.update.labelManagedBy`, { firstname: `` })"
                            :placeholder="$t(`goals.update.placeholderManagedBy`)"
                            :data="usersAll"
                            :on-select="typeaheadManagedByOnSelect"
                            :on-input-change="typeaheadManagedByOnInputChange"
                        />
                        <l-input-text
                            v-else
                            type="text"
                            :label="$t(`goals.update.labelManagedBy`, { firstname: `` })"
                            :value="goalManagedByName"
                            :on-click="removeManager"
                            default-padding
                        />
                    </div>
                    <!--/Managed By -->
                </div>

                <!-- Visibility -->
                <div class="col-md-5">
                    <l-select
                        v-model="goal.visibility"
                        :options="optionsVisibility"
                        :disabled="!goal.access.canUpdateGoal"
                    />
                </div>
                <!--/Visibility -->

                <!-- Contributors -->
                <div class="col-md-3">
                    <div v-show="!showContributors" style="margin-top: -10px">
                        <button
                            v-b-popover.hover.top="$t('goals.update.helpOwner')"
                            class="btn btn-default btn-block btn-raised"
                            @click="showContributors = true"
                        >
                            <i class="fal fa-plus"></i>
                            {{ $t(`goals.update.btnAddContributor`) }}
                        </button>
                    </div>
                    <l-input-typeahead
                        v-show="showContributors"
                        style="margin-top: 0;"
                        :disabled="!goal.access.canUpdateGoal"
                        :label="``"
                        :placeholder="$t(`goals.update.placeholderContributor`)"
                        :data="contributors"
                        :on-select="typeaheadContributorOnSelect"
                        :on-input-change="typeaheadContributorOnInputChange"
                    />
                </div>
                <!--/Contributors -->
            </div>

            <!-- Visible To -->
            <div
                v-show="goal.visibleTo.length || (goal.visibleToTeam && goal.visibleToTeam.length)"
                v
            >
                <span
                    v-for="(user, index) in goal.visibleTo"
                    :key="user._id"
                    class="r-default-margin-s"
                >
                    <span
                        class="label label-primary clickable"
                        tabindex="0"
                        @click="removeSelectedUser(index)"
                        @keyup.enter="removeSelectedUser(index)"
                    >
                        {{ user.firstname }} {{ user.lastname }}
                        <i class="fal fa-times l-default-margin-s"></i>
                    </span>
                </span>

                <span
                    v-for="(team, index) in goal.visibleToTeam"
                    :key="team._id"
                    class="r-default-margin-s"
                >
                    <span
                        class="label label-primary clickable"
                        tabindex="0"
                        @click="removeSelectedTeam(index)"
                        @keyup.enter="removeSelectedTeam(index)"
                    >
                        {{ team.name }}
                        <i class="fal fa-times l-default-margin-s"></i>
                    </span>
                </span>
            </div>
        </div>
        <!--/Owner -->

        <!-- Key Results -->
        <div @mouseover="helpSection = 'kr'" @focus="helpSection = 'kr'">
            <h5 class="color-dark">
                <strong>{{ $t(`goals.update.titleMetrics`) }}</strong>
                <span
                    v-b-popover.hover.top="$t('goals.update.helpKR')"
                    class="tw-text-gray-400 tw-mr-2 tw-ml-4"
                >
                    <i class="fal fa-info-circle"></i>
                </span>
                <span class="tw-text-gray-400">
                    <i class="fal fa-hand-pointer tw-mr-2 tw-ml-4"></i>
                    {{ $t("goals.update.labelDragNDrop") }}
                </span>
            </h5>

            <draggable
                :list="keyResults"
                :group="{ name: 'key-results' }"
                v-bind="{
                    animation: 200,
                    group: 'description',
                    disabled: false,
                    ghostClass: 'ghost',
                }"
                filter=".form-group"
                :prevent-on-filter="false"
                tag=""
                class="dropzone"
                style="padding: 0;"
                @change="onOrderChange"
            >
                <transition-group>
                    <div
                        v-for="keyResult in keyResults"
                        :key="keyResult._id"
                        class="tw-border tw-border-solid tw-border-gray-200 tw-rounded-lg tw-pt-4 tw-pr-4 tw-pl-4 tw-mb-4"
                    >
                        <l-goal-details-key-result-edit
                            :goal="goal"
                            :key-result="keyResult"
                            @remove="removeKeyResult"
                            @update="updateKeyResult"
                            @warning="handleWarning"
                        />
                    </div>
                </transition-group>
            </draggable>

            <div class="row">
                <div class="col-md-3">
                    <button
                        class="btn btn-default btn-block btn-raised"
                        :disabled="goal.dynamicProgressEnabled"
                        @click="addKeyResult()"
                    >
                        <i class="fal fa-plus"></i>
                        <span>{{
                            user.teamSeetings &&
                            user.teamSeetings.customWording &&
                            user.teamSeetings.customWording.goalsAddKR
                                ? user.teamSeetings.customWording.goalsAddKR
                                : $t(`goals.update.btnAddKR`)
                        }}</span>
                    </button>
                </div>
                <div v-show="user.rights.G_VIEW_TREE && !goal.isDevelopmentGoal" class="col-md-6">
                    <button
                        v-show="
                            goal.keyResults && !goal.keyResults.some(kr => kr.type === 'keyResult')
                        "
                        v-b-popover.hover.top="$t('goals.update.helpDynamicProgress')"
                        class="btn btn-default btn-raised"
                        @click="changeEnableDP()"
                    >
                        <i
                            :class="[
                                goal.dynamicProgressEnabled
                                    ? 'fal fa-check-square'
                                    : 'fal fa-square',
                            ]"
                        ></i>
                        <span>{{ $t(`goals.update.btnEnableDynamicProgress`) }}</span>
                    </button>
                </div>
            </div>

            <div v-show="checkKeyResultWeightsWarning">
                <div class="hr-small"></div>
                <div class="panel panel-warning">
                    <div class="panel-heading">
                        {{ $t(`goals.update.helpKeyResultsWeight`) }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Initiatives -->
        <div v-show="user.rights.G_INITIATIVES">
            <div @mouseover="helpSection = 'initiatives'" @focus="helpSection = 'initiatives'">
                <h5 class="color-dark">
                    <strong>{{ $t(`goals.update.titleInitiatives`) }}</strong>
                    <span
                        v-b-popover.hover.top="$t('goals.update.helpInitiatives')"
                        class="tw-text-gray-400 tw-mr-2 tw-ml-4"
                    >
                        <i class="fal fa-info-circle"></i>
                    </span>
                    <span class="tw-text-gray-400">
                        <i class="fal fa-hand-pointer tw-mr-2 tw-ml-4"></i>
                        {{ $t("goals.update.labelDragNDrop") }}
                    </span>
                </h5>

                <draggable
                    :list="initiatives"
                    :group="{ name: 'initiatives' }"
                    v-bind="{
                        animation: 200,
                        group: 'description',
                        disabled: false,
                        ghostClass: 'ghost',
                    }"
                    filter=".form-group"
                    :prevent-on-filter="false"
                    tag=""
                    class="dropzone"
                    style="padding: 0;"
                    @change="onOrderChange"
                >
                    <transition-group>
                        <div
                            v-for="keyResult in initiatives"
                            :key="keyResult._id"
                            class="tw-border tw-border-solid tw-border-gray-200 tw-rounded-lg tw-p-4 tw-mb-4"
                        >
                            <l-goal-details-key-result-edit
                                :goal="goal"
                                :key-result="keyResult"
                                @remove="removeKeyResult"
                                @update="updateKeyResult"
                            />
                        </div>
                    </transition-group>
                </draggable>

                <div class="row">
                    <div class="col-md-3">
                        <button
                            class="btn btn-default btn-block btn-raised"
                            @click="addInitiative()"
                        >
                            <i class="fal fa-plus"></i>
                            <span>{{ $t(`goals.update.btnAddInitiative`) }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <!--/Initiatives -->

        <!-- Development Goal -->
        <div
            v-show="
                goal.isDevelopmentGoal &&
                    ['goalMe', 'goalOtherUser', 'goalDirectReport'].includes(goalType) &&
                    (user.rights.R_VIEW || user.rights.F_VIEW) &&
                    user.rights.G_SKILLS
            "
        >
            <div @mouseover="helpSection = 'development'" @focus="helpSection = 'development'">
                <h5 class="color-dark">
                    <strong>
                        {{ $t(`goals.update.titleDevelopmentGoal`) }}
                    </strong>
                    <span
                        v-b-popover.hover.top="$t('goals.update.helpDevelopment')"
                        class="tw-text-gray-400 tw-mr-2 tw-ml-4"
                    >
                        <i class="fal fa-info-circle"></i>
                    </span>
                </h5>

                <div>
                    <l-input-typeahead
                        :label="``"
                        :placeholder="$t(`goals.update.placeholderDevelopmentGoal`)"
                        :data="skills"
                        :show-picture="false"
                        :on-select="typeaheadSkillOnSelect"
                        :on-input-change="typeaheadSkillOnInputChange"
                    />

                    <div v-show="goal.skills && goal.skills.length">
                        <span v-for="skill in goal.skills" :key="skill._id">
                            <span
                                class="label label-primary clickable r-default-margin-s"
                                tabindex="0"
                                @click="removeSkill(skill)"
                                @keyup.enter="removeSkill(skill)"
                            >
                                <span class="r-default-margin-s">
                                    {{ skill.name }}
                                </span>
                                <i class="fal fa-times"></i>
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <!--/Development Goal -->

        <!-- Jira -->
        <div v-show="jiraEnabled">
            <h5 class="color-dark">
                <strong>{{ $t(`goals.update.titleLinkJiraIssues`) }} </strong>
            </h5>

            <div>
                <l-input-typeahead
                    :label="``"
                    :placeholder="$t(`goals.update.placeholderJiraIssue`)"
                    :data="jiraIssues"
                    :on-select="typeaheadJiraOnSelect"
                    :on-input-change="typeaheadJiraOnInputChange"
                    :show-picture="false"
                />

                <div v-show="goal.jiraIssues && goal.jiraIssues.length">
                    <span
                        v-for="jiraIssue in goal.jiraIssues"
                        :key="jiraIssue.key"
                        class="r-default-margin-s"
                    >
                        <span
                            class="label label-primary clickable"
                            tabindex="0"
                            @click="unlinkJiraIssue(jiraIssue)"
                            @keyup.enter="unlinkJiraIssue(jiraIssue)"
                        >
                            {{ jiraIssue.key }}: {{ jiraIssue.summaryText }}
                            <i class="fal fa-times r-default-margin-s"></i>
                        </span>
                    </span>
                </div>
                <l-space v-show="!(goal.jiraIssues && goal.jiraIssues.length)" :size="1" />
            </div>
        </div>
        <!--/Jira -->

        <!-- Alignment -->
        <div v-show="user.rights.G_VIEW_TREE && !goal.isDevelopmentGoal">
            <div @mouseover="helpSection = 'alignment'" @focus="helpSection = 'alignment'">
                <h5 class="color-dark">
                    <strong>
                        {{ $t(`goals.update.titleAlignment`) }}
                    </strong>
                    <span
                        v-b-popover.hover.top="$t('goals.update.helpAlignment')"
                        class="tw-text-gray-400 tw-mr-2 tw-ml-4"
                    >
                        <i class="fal fa-info-circle"></i>
                    </span>
                </h5>

                <div
                    v-if="!(goal.parent && goal.parent._id) && goal.access.canUpdateGoal"
                    class="row"
                >
                    <div class="col-md-3">
                        <button
                            :disabled="showParentGoalSelectionModal"
                            href="#"
                            class="btn btn-default btn-block btn-raised tw-px-4"
                            @click.prevent="showParentGoalSelectionModal = true"
                        >
                            <i class="fal fa-plus" aria-hidden="true"></i>
                            {{ $t("goals.update.btnAddParentGoal") }}
                        </button>
                    </div>
                </div>

                <div v-else style="display: flex; align-items: center;">
                    <l-input-text
                        style="flex-grow: 1;"
                        type="text"
                        :label="``"
                        :value="goal.parent.name"
                        disabled
                    />
                    <span
                        class="color-primary l-default-margin-s clickable"
                        style="font-size: 18px;"
                        tabindex="0"
                        @click="removeParent()"
                        @keyup.enter="removeParent()"
                    >
                        <i class="fal fa-trash"></i>
                    </span>
                </div>
            </div>
        </div>
        <!--/Alignment -->

        <!-- Goal Quality -->
        <div v-if="goalQuality && goalQuality.totalScore" class="tw-mt-6">
            <l-progress
                :progress-percent="goalQuality.totalScore"
                progress-bar-class="progress-bar-primary"
                size="sm"
                hide-popover
                class="tw-mb-4"
            />

            <div class="tw-text-gray-500">
                <span v-show="!goalQuality.recommendations.length">
                    {{ $t(`goals.update.labelGoalQuality`) }}
                </span>
                <span v-show="goalQuality.recommendations.length">
                    {{ $t(`goals.update.labelOptimizationIdeas`) }}:
                </span>

                <span
                    v-for="(recommendation, index) in goalQuality.recommendations"
                    :key="recommendation.icon"
                    class="r-default-margin-s"
                >
                    <span v-if="index">, </span>{{ recommendation.text }}
                </span>
            </div>

            <hr />
        </div>
        <hr v-else />
        <!--/Goal Quality -->

        <!-- Error -->
        <div v-if="error">
            <div class="panel panel-warning">
                <div class="panel-heading">
                    <span>{{ error.message }}</span>
                </div>
            </div>
            <hr />
        </div>
        <!--/Error -->

        <div class="row">
            <div class="col-md-6">
                <!-- Save as draft (when editing) -->
                <button
                    v-show="goal.status === 'draft' && goal.access.canUpdateGoal"
                    class="btn btn-primary btn-block"
                    @click="
                        () =>
                            updateGoal(goal)
                                .then(() => $router.replace(`/goals/details/${goal._id}`))
                                .catch(() => {})
                    "
                >
                    {{ $t(`goals.update.btnSaveDraft`) }}
                </button>
            </div>

            <div class="col-md-6">
                <!-- Submit -->
                <button
                    v-if="goal.access.canUpdateGoal"
                    class="btn btn-primary btn-raised btn-block"
                    @click="
                        () =>
                            updateGoal({ ...goal, status: 'live' })
                                .then(() => {
                                    $router.replace(`/goals/details/${goal._id}`);
                                })
                                .catch(() => {})
                    "
                >
                    {{
                        goalType === "goalMe" &&
                        goal.managedBy._id !== user._id &&
                        goal.visibility !== "me"
                            ? $t(`goals.update.btnSubmit`)
                            : $t(`goals.update.btnSaveActivate`)
                    }}
                </button>
            </div>
        </div>
        <!--/ Other settings -->

        <!-- Parent Goal Selection Modal -->
        <l-modal
            v-if="showParentGoalSelectionModal"
            size="l"
            @close="showParentGoalSelectionModal = false"
        >
            <template #header>{{ $t("goals.update.titleParentGoal") }}</template>
            <template #body>
                <l-goal-parent-select
                    :excluded-goal-id="goal._id"
                    :selected-parent-goal.sync="goal.parent"
                    @update:selectedParentGoal="showParentGoalSelectionModal = false"
                />
            </template>
        </l-modal>
        <!-- /Parent Goal Selection Modal -->
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import debounce from "lodash.debounce";
import ObjectId from "bson-objectid";

import { icons } from "@/config/goal-icons";

import format from "@/utils/format-typeahead";
import { alphabetically } from "@/utils/sort-object";

import LDatePicker from "@/components/LDatePicker";
import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LProgress from "@/components/LProgress";
import LSelect from "@/components/LSelect";
import LSpace from "@/components/LSpace";
import LModal from "@/components/LModal";
import LGoalParentSelect from "@/components/LGoalParentSelect";
import LGoalDetailsKeyResultEdit from "@/components/LGoalDetailsKeyResultEdit";
import Draggable from "vuedraggable";

import congratsModalImage from "@/img/icons/progress.png";
import teamPicture from "@/img/icons/team-icon.png";

export default {
    components: {
        LDatePicker,
        LInputText,
        LInputTypeahead,
        LProgress,
        LSelect,
        LSpace,
        LModal,
        LGoalParentSelect,
        Draggable,
        LGoalDetailsKeyResultEdit,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            goal: {},
            goalQuality: {},
            goalType: "", // We need another variable to host the goal type, because it doesn't match 1:1 with our data
            keyResults: [],
            initiatives: [],
            error: null,

            autoSaving: false,

            skills: [],

            freezeGoalCheck: false,
            unfreezeGoalCheck: false,
            removeGoalCheck: false,

            parentGoalNoResults: false,

            helpSection: "title", // "title" | "owner" | "kr" | "initiatives" | "alignment" | "development"

            showContributors: false,
            showManagedBy: false,

            checkKeyResultWeightsWarning: false,

            showCongratsModal: false,
            showParentGoalSelectionModal: false,

            // FIXME: we need to manually keep track and filter the list of local teams because the API for search doesn't accept fitlers, and the API
            // for lists (which accept filters) doesn't accept search.
            localTeamQuery: "",
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
            getGoalDetails: "goals/goals/details",
            cycles: "goals/cycles/list",
            getUserList: "users/list/list",
            usersSearchList: "users/search/list",
            teamsSearchList: "teams/search/list",
            localTeamsList: "teams/local/list",
            tagsSearchList: "tags/search/list",
            goalsSearchList: "goals/search/list",
            getSkillsByUser: "skills/getSkillsByUser",
            jiraStatus: "jira/status",
            jiraSearchList: "jira/list",
        }),

        tags() {
            return this.tagsSearchList.map(tag => ({
                _id: tag._id,
                name: tag.name,
                desc: "",
            }));
        },
        users() {
            let list = this.usersSearchList;

            return list.map(format.users);
        },
        reports() {
            let list = this.usersSearchList
                // Filter out current user
                .filter(user => user._id !== this.user._id);

            if (!list.length) {
                // Note: we could also directly use the key "reports", but we will need to make another API call, when we can
                // simply filter the "directTeam" list based on the user's relationship.
                list = this.getUserList({ key: "directTeam" }).filter(
                    user => user.relationship === "directReport",
                );
            }

            return list.map(format.users);
        },
        usersAll() {
            let list = this.usersSearchList;

            if (!list.length) {
                list = this.getUserList({ key: "directTeam" });
            }

            return list.map(format.users);
        },
        contributors() {
            let list = this.usersSearchList
                // Filter out selected users
                .filter(user => !this.goal.visibleTo.map(u => u._id).includes(user._id));

            if (!list.length) {
                list = this.getUserList({ key: "directTeam" })
                    // Filter out selected users
                    .filter(user => !this.goal.visibleTo.map(u => u._id).includes(user._id));
            }

            return [
                ...list.map(format.users),
                ...this.teamsSearchList.map(team => ({
                    _id: team._id,
                    type: "team",
                    name: team.name,
                    desc: this.$t("typeahead.team"),
                    picture: teamPicture,
                })),
            ];
        },
        localTeams() {
            return this.localTeamsList
                .filter(team => team.name.toLowerCase().includes(this.localTeamQuery.toLowerCase()))
                .map(team => ({
                    _id: team._id,
                    name: team.name,
                }));
        },
        goalManagedByName() {
            if (
                this.goal.managedBy &&
                this.goal.managedBy.firstname &&
                this.goal.managedBy.lastname
            ) {
                return this.goal.managedBy.firstname + ` ` + this.goal.managedBy.lastname;
            }

            return "";
        },
        goalUserName() {
            if (this.goal.user && this.goal.user.firstname && this.goal.user.lastname) {
                return this.goal.user.firstname + ` ` + this.goal.user.lastname;
            }

            return "";
        },
        goalTeamName() {
            return this.goal.localTeam?.name ?? "";
        },
        jiraIssues() {
            return this.jiraSearchList.map(format.jiraIssues);
        },

        optionsCycle() {
            const options = this.user.rights.G_CYCLE_ADMIN
                ? Array.from(this.cycles)
                : this.cycles
                      // Filter past cycles
                      .filter(cycle => new Date(cycle.timeline.end.date) > new Date())
                      // Filter frozen cycles
                      .filter(
                          cycle =>
                              cycle.settings.freezeGoalsWhenActive === false ||
                              (cycle.settings.freezeGoalsWhenActive === true &&
                                  new Date(cycle.timeline.active.date) > new Date()),
                      );

            if (this.user.rights.G_CYCLE_ADMIN || (!this.goal?.isNew && !this.goal?.cycle?._id)) {
                options.unshift({ _id: "", name: this.$t(`goals.update.optionNoCycle`) });
            }

            return options.map(cycle => ({
                value: cycle._id,
                text: cycle.name,
            }));
        },
        optionsType() {
            const types = [];

            types.push({
                value: "goalMe",
                text: this.$t(`goals.update.optionGoalMe`),
            });

            types.push({
                value: "goalDirectReport",
                text: this.$t(`goals.update.optionGoalDirectReport`),
            });

            if (this.user.rights.G_ASSIGN_OTHER) {
                types.push({
                    value: "goalOtherUser",
                    text: this.$t(`goals.update.optionGoalOtherUser`),
                });
            }

            if (
                this.user.rights.G_VIEW_TREE &&
                this.localTeamsList?.length &&
                !this.goal.isDevelopmentGoal
            ) {
                types.push({
                    value: "goalLocalTeam",
                    text: this.$t(`goals.update.optionGoalLocalTeam`),
                });
            }

            if (
                this.user.rights.G_CREATE_TEAM &&
                this.user.rights.G_VIEW_TREE &&
                !this.goal.isDevelopmentGoal
            ) {
                types.push({
                    value: "goalTeam",
                    text: this.$t(`goals.update.optionGoalTeam`),
                });
            }

            return types;
        },
        optionsVisibility() {
            switch (this.goalType) {
                case "goalMe":
                case "goalUser": {
                    const options = [
                        { value: "me", text: this.$t(`goals.update.optionVisibilityMe`) },
                        { value: "limited", text: this.$t(`goals.update.optionVisibilityLimited`) },
                    ];

                    if (!this.user.rights.G_DEACTIVATE_PUBLIC) {
                        options.push({
                            value: "all",
                            text: this.$t(`goals.update.optionVisibilityPublic`),
                        });
                    }

                    return options;
                }

                case "goalDirectReport":
                case "goalOtherUser": {
                    const options = [
                        {
                            value: "limited",
                            text: this.$t(`goals.update.optionVisibilityLimitedUser`),
                        },
                    ];

                    if (!this.user.rights.G_DEACTIVATE_PUBLIC) {
                        options.push({
                            value: "all",
                            text: this.$t(`goals.update.optionVisibilityPublic`),
                        });
                    }

                    return options;
                }

                case "goalLocalTeam":
                    return [
                        { value: "all", text: this.$t(`goals.update.optionVisibilityPublic`) },
                        {
                            value: "limited",
                            text: this.$t(`goals.update.optionVisibilityLocalTeam`),
                        },
                    ];

                case "goalTeam":
                    return [{ value: "all", text: this.$t(`goals.update.optionVisibilityPublic`) }];

                default:
                    return [];
            }
        },
        selectedParent() {
            return Boolean(this.goal?.parent?._id);
        },
        jiraEnabled() {
            return Boolean(this.jiraStatus?.enabled && this.jiraStatus?.accountId);
        },
    },
    watch: {
        id: {
            immediate: true,
            handler(id) {
                this.goal = this.getGoalDetails(id);
                this.goalType = this.getGoalType();
                this.updateQualityScore();

                if (this.goal?.user?._id) {
                    this.$store
                        .dispatch({
                            type: "skills/getListForUser",
                            payload: { userId: this.goal.user._id, showAll: true },
                        })
                        .then(() => {
                            // Note: we already verify that the user is set a few lines above before making the request,
                            // but we need to verify again that the user is set as this code runs asynchronously
                            // and by the time we get here, there might not be a user anymore ...
                            if (this.goal?.user?._id) {
                                this.skills = this.getSkillsByUser(this.goal.user._id).map(
                                    format.skills,
                                );
                            } else {
                                this.skills = [];
                            }
                        });
                } else {
                    this.skills = [];
                }
            },
        },
        goal: {
            deep: true,
            handler: debounce(function handler(goal) {
                this.updateGoal({ ...goal, autoSaved: true });
            }, 1000),
        },
        "goal.keyResults": {
            immediate: true,
            handler(items) {
                if (!Array.isArray(items)) return;

                // differentiate data coming from db into two sections
                this.keyResults = items.filter(({ type }) => type !== "initiative");
                this.initiatives = items.filter(({ type }) => type === "initiative");
            },
        },
    },
    created() {
        this.$emit("section", "goal"); // This is a bit hacky ...

        this.icons = icons;
        this.congratsModalImage = congratsModalImage;

        // Select first cycle by default if new goal and no cycle passed
        if (this.goal.isNew) {
            const cycle = this.goal.cycle?._id || this.optionsCycle?.[0]?.value || null;

            this.selectCycle(cycle);
        }

        // Fetch corresponding local team list
        if (this.user.rights.G_CREATE_TEAM || this.user.rights.G_CREATE_LOCALTEAM_ANY) {
            this.$store.dispatch({
                type: "teams/local/getList",
                payload: { filter: "allStacked" },
            });
        } else {
            this.$store.dispatch({
                type: "teams/local/getList",
                payload: { filter: "managerStacked" },
            });
        }
    },
    methods: {
        onOrderChange() {
            this.$set(this.goal, "keyResults", [...this.keyResults, ...this.initiatives]);
            this.autoSave(this.goal);
        },
        toggleDevelopmentGoal(isDevelopmentGoal) {
            this.$set(this.goal, "isDevelopmentGoal", isDevelopmentGoal);
        },
        getGoalType() {
            let skillsRequest = new Promise(resolve => resolve());
            let type = "";

            if (
                this.goal.type === "goalUser" &&
                this.goal.user &&
                this.goal.user._id === this.user._id
            ) {
                type = "goalMe";
                skillsRequest = this.$store.dispatch({
                    type: "skills/getListForUser",
                    payload: { userId: this.goal.user._id, showAll: true },
                });
            } else if (
                this.goal.type === "goalUser" &&
                this.goal.user &&
                this.goal.user._id !== this.user._id &&
                this.goal.managedBy &&
                this.goal.managedBy._id === this.user._id
            ) {
                type = "goalDirectReport";
                skillsRequest = this.$store.dispatch({
                    type: "skills/getListForUser",
                    payload: { userId: this.goal.user._id, showAll: true },
                });
            } else if (this.goal.type === "goalUser") {
                type = "goalOtherUser";

                if (this.goal?.user?._id) {
                    skillsRequest = this.$store.dispatch({
                        type: "skills/getListForUser",
                        payload: { userId: this.goal.user._id, showAll: true },
                    });
                }
            } else if (this.goal.type === "goalLocalTeam") {
                type = "goalLocalTeam";
            } else {
                type = "goalTeam";
            }

            skillsRequest.then(() => {
                this.skills = this.goal?.user?._id
                    ? this.getSkillsByUser(this.goal.user._id).map(format.skills)
                    : [];
            });

            return type;
        },

        typeaheadTagsOnSelect(tag) {
            if (!this.goal.tags.find(t => t._id === tag._id)) {
                this.goal.tags.push(tag);
            }
        },
        typeaheadTagsOnInputChange(query) {
            this.$store.dispatch("tags/search/findByName", { query });
        },
        removeTag(index) {
            this.goal.tags.splice(index, 1);
        },

        selectCycle(cycle) {
            if (!cycle) {
                this.goal.cycle = null;
                return;
            }

            const selectedCycle = this.cycles.filter(c => c._id === cycle);
            if (!selectedCycle || !selectedCycle.length) {
                return;
            }

            this.goal.cycle = { _id: selectedCycle[0]._id };

            // Update deadline
            if (selectedCycle[0]?.defaults?.deadline) {
                this.goal.deadline = new Date(selectedCycle[0].defaults.deadline);
            }

            // Append default tags
            if (selectedCycle[0]?.defaults?.tags?.length) {
                this.goal.tags = (this.goal.tags || [])
                    .concat(selectedCycle[0].defaults.tags)
                    .sort(alphabetically("name"))
                    .filter((item, index, arr) => !index || item._id !== arr[index - 1]._id);
            }
        },

        setIcon(icon) {
            this.goal.icon = `/img/icons/${icon}.png`;
        },

        selectType(type) {
            if (type === "goalMe") {
                this.goal.type = "goalUser";
                this.goal.visibility =
                    this.user.rights.G_PUBLIC_BY_DEFAULT && !this.user.rights.G_DEACTIVATE_PUBLIC
                        ? "all"
                        : "limited";
                this.goal.user = this.user;
                this.goal.localTeam = null;

                if (this.user.rights.G_MANAGED_DISABLED) {
                    this.goal.managedBy = this.user;
                } else {
                    this.goal.managedBy = {
                        _id: this.user.teamRole.reportsTo
                            ? this.user.teamRole.reportsTo
                            : this.user._id,
                    };
                }
            } else if (type === "goalDirectReport") {
                this.goal.type = "goalUser";
                this.goal.visibility =
                    this.user.rights.G_PUBLIC_BY_DEFAULT && !this.user.rights.G_DEACTIVATE_PUBLIC
                        ? "all"
                        : "limited";
                this.goal.managedBy = this.user;
                this.goal.user = null;
                this.goal.localTeam = null;
            } else if (type === "goalOtherUser") {
                this.goal.type = "goalUser";
                this.goal.visibility =
                    this.user.rights.G_PUBLIC_BY_DEFAULT && !this.user.rights.G_DEACTIVATE_PUBLIC
                        ? "all"
                        : "limited";
                this.goal.managedBy = this.user;
                this.goal.user = null;
                this.goal.localTeam = null;
            } else if (type === "goalLocalTeam") {
                this.goal.type = "goalLocalTeam";
                this.goal.visibility = "all";
                this.goal.managedBy = this.user;
                this.goal.user = null;
                this.goal.localTeam = null;
            } else if (type === "goalTeam") {
                this.goal.type = "goalTeam";
                this.goal.visibility = "all";
                this.goal.managedBy = this.user;
                this.goal.user = null;
                this.goal.localTeam = null;
            }

            // When the type of the goal changes, we update the list of potential parent goals
            this.$store.dispatch({
                type: "goals/search/findByName",
                payload: { type, exclude: this.goal._id },
            });

            // Update list of skills
            if (this.goal?.user?._id) {
                this.$store
                    .dispatch({
                        type: "skills/getListForUser",
                        payload: { userId: this.goal.user._id, showAll: true },
                    })
                    .then(() => {
                        this.skills = this.getSkillsByUser(this.goal.user._id).map(format.skills);
                    });
            }

            this.showManagedBy = false;
        },

        typeaheadLocalTeamOnSelect(team) {
            this.goal.localTeam = team;

            this.localTeamQuery = "";
        },
        typeaheadLocalTeamOnInputChange(query) {
            this.localTeamQuery = query;
        },

        typeaheadReportsOnSelect(user) {
            this.goal.user = user;
            this.$store.dispatch("users/search/reset");
        },
        typeaheadReportsOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", {
                    query,
                    sort: "indirectReportsSearch",
                });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },
        typeaheadUserOnSelect(user) {
            this.goal.user = user;
            this.$store.dispatch("users/search/reset");
        },
        typeaheadUserOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },
        removeOwner(ref) {
            this.goal.user = undefined;
            this.goal.localTeam = undefined;
            this.selectedLocalTeam = "";

            if (ref !== "input-report") {
                this.goal.managedBy = undefined;
                this.goal.owner = "";
                this.selectedOwner = "";
            }

            // Reset typeahead
            this.$store.dispatch("users/search/reset");
            this.localTeamQuery = "";

            // Automatically focus the typeahead input
            this.$nextTick(() => {
                this.$refs?.[ref]?.$refs?.["typeahead"]?.$refs?.["input"]?.focus();
            });
        },

        typeaheadManagedByOnSelect(user) {
            this.goal.managedBy = user;
            this.$store.dispatch("users/search/reset");
        },
        typeaheadManagedByOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },
        removeManager() {
            this.goal.managedBy = undefined;

            // Automatically focus the typeahead input
            this.$nextTick(() => {
                this.$refs?.["input-managed-by"]?.$refs?.["typeahead"]?.$refs?.["input"]?.focus();
            });
        },

        typeaheadContributorOnSelect(userOrTeam) {
            if (userOrTeam.type === "team") {
                if (!this.goal.visibleToTeam) {
                    this.goal.visibleToTeam = [];
                }
                this.goal.visibleToTeam.push(userOrTeam);
            } else {
                this.goal.visibleTo.push(userOrTeam);
            }
            this.$store.dispatch("users/search/reset");
            this.$store.dispatch("teams/search/reset");
        },
        typeaheadContributorOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
                this.$store.dispatch("teams/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
                this.$store.dispatch("teams/search/reset");
            }
        },
        removeSelectedUser(index) {
            this.goal.visibleTo.splice(index, 1);
        },
        removeSelectedTeam(index) {
            this.goal.visibleToTeam.splice(index, 1);
        },

        changeEnableDP() {
            this.goal.dynamicProgressEnabled = !this.goal.dynamicProgressEnabled;
        },
        updateKeyResult(keyResult) {
            const index = this.goal.keyResults.findIndex(kr => kr._id === keyResult._id);

            if (index > -1) {
                this.goal.keyResults[index] = {
                    ...this.goal.keyResults[index],
                    ...keyResult,
                };

                this.autoSave(this.goal);
            }
        },
        addKeyResult() {
            this.goal.keyResults.push({
                _id: ObjectId().toString(),
                name: "",
                type: "keyResult",
                metricType: "percentage",
                metricEnd: "100",
                metricWeight: "",
                metricCurrent: "0",
            });
        },
        addInitiative() {
            this.goal.keyResults.push({
                _id: ObjectId().toString(),
                name: "",
                type: "initiative",
                metricType: "percentage",
                metricEnd: "100",
                metricWeight: "",
                metricCurrent: "0",
            });
        },
        removeKeyResult(keyResultId) {
            const index = this.goal.keyResults.findIndex(kr => kr._id === keyResultId);

            if (index > -1) {
                this.goal.keyResults.splice(index, 1);
            }
        },

        removeParent() {
            this.goal.parent = null;
        },

        typeaheadSkillOnSelect(skill) {
            if (!Array.isArray(this.goal?.skills)) {
                this.$set(this.goal, "skills", []);
            }

            if (!this.goal.skills.find(s => s._id === skill._id)) {
                this.goal.skills.push(skill);
            }

            if (this.goal?.user?._id) {
                this.skills = this.getSkillsByUser(this.goal.user._id).map(format.skills);
            } else {
                this.skills = [];
            }
        },
        typeaheadSkillOnInputChange(query) {
            if (query.length >= 2) {
                if (this.goal?.user?._id) {
                    this.skills = this.getSkillsByUser(this.goal.user._id)
                        .filter(skill => skill.name.toLowerCase().includes(query.toLowerCase()))
                        .map(format.skills);
                } else {
                    this.skills = [];
                }
            } else {
                if (this.goal?.user?._id) {
                    this.skills = this.getSkillsByUser(this.goal.user._id).map(format.skills);
                } else {
                    this.skills = [];
                }
            }
        },
        removeSkill(skill) {
            const index = this.goal.skills.findIndex(s => s._id === skill._id);

            if (index > -1) {
                this.goal.skills.splice(index, 1);
            }
        },

        typeaheadJiraOnSelect(issue) {
            if (!Array.isArray(this.goal.jiraIssues)) {
                this.goal.jiraIssues = [];
            }

            this.goal.jiraIssues.push(issue);
            this.$store.dispatch("jira/reset");
        },
        typeaheadJiraOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("jira/search", { payload: { query } });
            } else {
                this.$store.dispatch("jira/reset");
            }
        },
        unlinkJiraIssue(issue) {
            const index = this.goal.jiraIssues.findIndex(i => i.key === issue.key);

            if (index > -1) {
                this.goal.jiraIssues.splice(index, 1);
            }
        },

        async updateGoal(goal) {
            if (this.autoSaving) {
                return;
            }
            this.$refs["input-name"]?.validate();

            this.autoSaving = true;
            this.error = null;

            await this.$store
                .dispatch({
                    type: "goals/goals/update",
                    payload: {
                        goal: {
                            scope: "full", // "full" by default
                            ...goal,
                        },
                    },
                })
                .catch(err => {
                    this.error = { message: err?.response?.data?.message };
                    window.scrollTo({ top: 0, behavior: "smooth" });
                });

            this.updateQualityScore();
            this.autoSaving = false;

            if (this.error) {
                // Reject Promise so that thenable actions are not performed on error
                return Promise.reject(this.error.message);
            }
        },

        autoSave: debounce(function(goal) {
            this.updateGoal({
                ...goal,
                autoSaved: true,
            });
        }, 1000),

        updateQualityScore() {
            let totalScore = 100;
            let recommendations = [];

            // abort if title not set
            if (!this.goal.name || !this.goal.name.length) {
                this.goalQuality = {};
                return;
            }

            // check objective (shouldn't contain numbers)
            if (this.goal.name && /\d/.test(this.goal.name)) {
                totalScore -= 20;

                recommendations.push({
                    icon: "fal fa-flag",
                    text: this.$t("goals.update.helpAvoidMetricsTitle"),
                });
            }

            // check key results
            if (this.goal.keyResults) {
                var countMilestone = 0;
                var countTotal = 0;

                this.goal.keyResults.forEach(function(keyResult) {
                    countTotal++;

                    if (keyResult.metricType == "none" || keyResult.metricType == "new") {
                        countMilestone++;
                    }
                });

                if (countTotal > 5) {
                    totalScore -= 40;

                    recommendations.push({
                        icon: "fal fa-crosshairs",
                        text: this.$t("goals.update.helpLessKRs"),
                    });
                }

                if (!countTotal) {
                    totalScore -= 40;

                    recommendations.push({
                        icon: "fal fa-crosshairs",
                        text: this.$t("goals.update.helpMoreKRs"),
                    });
                }

                if (countTotal && countMilestone / countTotal > 0.33) {
                    totalScore -= 40;

                    recommendations.push({
                        icon: "fal fa-crosshairs",
                        text: this.$t("goals.update.helpMeasurableKRs"),
                    });
                }
            }

            this.goalQuality = { totalScore, recommendations };
        },

        handleWarning({ type, value }) {
            if (type === "weight") {
                this.checkKeyResultWeightsWarning = value;
            }
        },
    },
};
</script>

<style scoped>
.managed-by-top-margin {
    margin-top: 15px;
}
.type-select-bottom-margin {
    margin-bottom: 30px;
}
.dropzone >>> .ghost {
    opacity: 0.3;
}
</style>
